import api from '@/api';
import isEmpty from 'lodash/isEmpty';
import { store as RootStore } from '@/store/well-logs/index';
import { getSettingsFromWellProfile } from './libs/getSettingsFromWellProfile';
import { getWellLoggingKeysAndCurvesFromWellProfile } from './libs/getWellLoggingKeysFromWellProfile';

/*
    Общая модель для работы с состоянием скважин
*/

const initialState = {
    /*
        Список состояний
    */
    profiles: [],
    groups: [],
    /*
        Привязка состояний скважин к самим скважинам по их id
    */
    wellsByProfile: null,
    activeProfileId: null,
    isLoading: false,
    isError: null,
    activeGeoVersions: [],
};

export const profilesModel = {
    name: 'profiles',
    state: initialState,
    reducers: {
        setProfiles(state, payload) {
            return { ...state, profiles: payload.profiles };
        },
        setRequestState(state, payload) {
            return { ...state, isLoading: payload, isError: false };
        },
        setError(state) {
            return { ...state, isLoading: false, isError: true };
        },
        setActiveProfileId(state, activeProfileId) {
            return { ...state, activeProfileId: activeProfileId };
        },
        addProfile(state, payload) {
            const { profile } = payload;
            const { profiles } = state;

            if (profiles.length === 0 || !profiles.find(p => p?.profileId === profile?.profileId)) {
                profiles.push(profile);
            }

            return { ...state, profiles: profiles };
        },
        addProfileGroups(state, payload) {
            if (!payload) return state;

            const newState = {
                ...state,
                groups: [...payload],
            };

            return newState;
        },
        setActiveGeoVersions(state, payload) {
            const { versions } = payload;

            return { ...state, activeGeoVersions: versions };
        },
        updateProfileById(state, profile) {
            const { profiles } = state;
            const updatedProfiles =
                profiles.length < 0 ? profiles : profiles.map(p => (p.profileId === profile.profileId ? profile : p));
            return { ...state, profiles: updatedProfiles };
        },
    },
    effects: dispatch => ({
        /*
            Изменить настройки скважины согласно выбранному состоянию
        */
        applyProfileWellSettings(payload, rootState) {
            const { stateWell, wellKey } = payload;
            const { wellId } = stateWell;
            if (!stateWell || isEmpty(stateWell) || !wellKey) {
                // eslint-disable-next-line no-console
                console.log('Профайл скважины не был найден');
                return;
            }
            let defaultSettings = {};
            if (rootState.tablets.isGroup) {
                const activeTabletKey = rootState.tablets.list[rootState.tablets.activeGroupName][wellId]?.key;
                defaultSettings = rootState.tabletSettings[activeTabletKey];
            } else {
                defaultSettings = rootState.tabletSettings[rootState.lists.activeItemKey];
            }
            const { activeTemplateId } = rootState.templates;
            const { data } = rootState.researches;
            const researches = data[wellId];
            const settings = getSettingsFromWellProfile(stateWell, defaultSettings, rootState);
            const { geoVersions, columnsOrder } = settings;
            if (geoVersions?.length > 0) this.setActiveGeoVersions({ versions: geoVersions });

            let wellLoggingKeys = [];
            let curveSettings = {};
            let curveDisplaySettings = {};

            if (researches && !isEmpty(researches)) {
                const wellLoggingKeysAndCurvesFromStateWell = getWellLoggingKeysAndCurvesFromWellProfile(
                    researches,
                    stateWell,
                );

                wellLoggingKeys = wellLoggingKeysAndCurvesFromStateWell?.wellLoggingKeys ?? [];
                curveSettings = wellLoggingKeysAndCurvesFromStateWell?.curveSettings ?? {};
                curveDisplaySettings = wellLoggingKeysAndCurvesFromStateWell?.curveDisplaySettings ?? {};
            }

            const tabletSettingsPayload = {
                data: Object.assign({}, settings, {
                    wellLoggingKeys: wellLoggingKeys,
                    curveSettings: curveSettings,
                    prevTemplateId: activeTemplateId,
                    curveDisplaySettings: curveDisplaySettings,
                    columnsOrder: columnsOrder,
                }),
                wellKey: wellKey,
            };
            dispatch.tabletSettings.setTabletSettings(tabletSettingsPayload);
        },
        updateDisplaySettings(payload) {
            const { emptyColumnsDataSettings, globalCurvesSettings, depthType, isSyncScroll } = payload;
            const { updateDisplaySettings } = dispatch.displaySettings;
            if (typeof emptyColumnsDataSettings === 'boolean')
                updateDisplaySettings({ emptyColumnsDataSettings: emptyColumnsDataSettings });
            if (globalCurvesSettings) updateDisplaySettings({ globalCurvesSettings: globalCurvesSettings });
            updateDisplaySettings({ depthType: depthType, isSyncScroll: isSyncScroll });
        },
        async loadProfileGroups() {
            if (profilesModel.state.isLoading) {
                console.log('Already loading, skipping fetch...');
                return;
            }
            this.setRequestState(true);

            try {
                const profileGroups = await api.carottage.fetchProfilesGroupes();

                if (profileGroups) this.addProfileGroups(profileGroups.GroupProfiles);
            } catch (error) {
                console.error('error:loadProfileGroups', error);
            }
            this.setRequestState(false);
        },

        async saveProfile(payload) {
            if (payload.wells) {
                const body = JSON.stringify(payload);
                const updatedeProfile = await api.carottage.saveProfile(body);
                if (updatedeProfile.profileId === payload.profileId) this.updateProfileById(payload);
                else {
                    this.addProfile({ profile: { ...payload, profileId: updatedeProfile.profileId } });
                    this.setActiveProfileId(updatedeProfile.profileId);
                    this.saveInitProfileToLocaleStorage({
                        profile: { ...payload, profileId: updatedeProfile.profileId },
                    });
                }
            }
        },
        async saveInitProfileToLocaleStorage(payload) {
            const { profile } = payload;

            try {
                if (profile.profileId)
                    localStorage.setItem(`profileSettings:${profile.profileId}`, JSON.stringify(profile));
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error:profile_set_to_local_storage', error);
            }
        },
        async getProfileFromLocaleStorage(payload) {
            const { profileId } = payload;
            let parsedProfile = null;
            try {
                const profileString = localStorage.getItem(`profileSettings:${profileId}`);

                if (profileString) parsedProfile = JSON.parse(profileString);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error:profile_get_from_local_storage', error);
            }

            return parsedProfile;
        },
    }),
};
